import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * LINKS ART CUSTOMISATION (desktop detail zoom): on screens 750px and wider, clicking the enlarged image opens a
 * full-screen "detail view" of the full-resolution file at true pixel size. Drag to move around; click, the X
 * button or Esc returns to the fitted view. Phones/tablets under 750px keep the theme's own pinch-zoom. The
 * original click-to-close behaviour is restored on small screens. Search for "detail" to find all additions.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {HTMLElement | null} */
  #detail = null;

  #detailQuery = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.#bindDetailZoom();
    this.#detailQuery.addEventListener?.('change', this.#bindDetailZoom);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.#detailQuery.removeEventListener?.('change', this.#bindDetailZoom);
  }

  /**
   * Detail zoom wiring. The theme closes the dialog from a declarative `on:click="/close"` attribute on each image
   * item, handled by a capture listener on `document`, so it cannot be intercepted from inside the dialog. On
   * desktop we move that attribute aside (and put it back on small screens) and handle the click ourselves.
   */
  #bindDetailZoom = () => {
    const items = this.refs.media || [];
    const desktop = this.#detailQuery.matches;

    for (const item of items) {
      if (!(item instanceof HTMLElement) || !item.classList.contains('product-media-container--image')) continue;

      if (!item.dataset.detailBound) {
        item.dataset.detailBound = '1';
        item.addEventListener('click', (event) => this.#handleItemClick(item, event));
      }

      if (desktop) {
        const value = item.getAttribute('on:click');
        if (value) {
          item.dataset.closeOnClick = value;
          item.removeAttribute('on:click');
        }
      } else if (item.dataset.closeOnClick && !item.hasAttribute('on:click')) {
        item.setAttribute('on:click', item.dataset.closeOnClick);
      }
    }
  };

  /**
   * @param {HTMLElement} item
   * @param {MouseEvent} event
   */
  #handleItemClick(item, event) {
    if (!this.#detailQuery.matches) return; // small screens: the theme's own handler is active
    if (this.#detail) return;

    const target = event.target;
    if (target instanceof HTMLImageElement) {
      this.#openDetail(target, event);
    } else {
      this.close(); // click on the empty area around the image: same as before
    }
  }

  /**
   * @param {string} url
   * @param {number} width
   */
  #withWidth(url, width) {
    try {
      const u = new URL(url, window.location.href);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  /**
   * Opens the full-screen detail view for an image.
   *
   * @param {HTMLImageElement} img - The enlarged image that was clicked.
   * @param {MouseEvent} event - The click event (used to centre the detail view on the clicked point).
   */
  #openDetail(img, event) {
    const rect = img.getBoundingClientRect();
    const rx = rect.width ? Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)) : 0.5;
    const ry = rect.height ? Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)) : 0.5;

    ensureDetailStyles();

    const overlay = document.createElement('div');
    overlay.className = 'detail-zoom';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', 'Detail view');
    overlay.tabIndex = -1;

    const detailImage = document.createElement('img');
    detailImage.className = 'detail-zoom__image';
    detailImage.alt = img.alt || '';
    detailImage.decoding = 'async';
    detailImage.hidden = true;

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'detail-zoom__close';
    closeButton.setAttribute('aria-label', 'Back to full view');
    closeButton.textContent = '×';

    const hint = document.createElement('p');
    hint.className = 'detail-zoom__hint';
    hint.textContent = 'Loading full detail…';

    overlay.append(detailImage, closeButton, hint);
    this.refs.dialog.append(overlay);
    this.#detail = overlay;
    closeButton.focus({ preventScroll: true });

    let moved = false;
    let pointerStart = /** @type {{x:number,y:number,left:number,top:number} | null} */ (null);

    overlay.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0 || e.target === closeButton) return;
      pointerStart = { x: e.clientX, y: e.clientY, left: overlay.scrollLeft, top: overlay.scrollTop };
      moved = false;
      try {
        overlay.setPointerCapture(e.pointerId);
      } catch (error) {
        // dragging still works without pointer capture
      }
      overlay.classList.add('detail-zoom--dragging');
    });
    overlay.addEventListener('pointermove', (e) => {
      if (!pointerStart) return;
      const dx = e.clientX - pointerStart.x;
      const dy = e.clientY - pointerStart.y;
      if (Math.abs(dx) + Math.abs(dy) > 5) moved = true;
      overlay.scrollLeft = pointerStart.left - dx;
      overlay.scrollTop = pointerStart.top - dy;
    });
    const endDrag = () => {
      pointerStart = null;
      overlay.classList.remove('detail-zoom--dragging');
    };
    overlay.addEventListener('pointerup', endDrag);
    overlay.addEventListener('pointercancel', endDrag);
    overlay.addEventListener('click', (e) => {
      e.stopPropagation();
      if (moved) {
        moved = false; // the click that ends a drag should not close the view
        return;
      }
      this.#closeDetail();
    });

    // Show the largest version Shopify will deliver (it never upscales), at true pixel size on this screen.
    const baseUrl = img.getAttribute('data_max_resolution') || img.currentSrc || img.src;
    const fullUrl = this.#withWidth(baseUrl, 5760);
    const loader = new Image();
    loader.onload = () => {
      if (this.#detail !== overlay) return; // closed while loading
      const dpr = window.devicePixelRatio || 1;
      const naturalWidth = loader.naturalWidth;
      // true pixel size, but never less than 1.5x the screen width (so there is always something to zoom into)
      const cssWidth = Math.max(naturalWidth / dpr, window.innerWidth * 1.5);
      const cssHeight = cssWidth * (loader.naturalHeight / naturalWidth);
      detailImage.src = fullUrl;
      detailImage.style.width = `${cssWidth}px`;
      detailImage.style.height = `${cssHeight}px`;
      detailImage.hidden = false;
      overlay.scrollLeft = rx * cssWidth - overlay.clientWidth / 2;
      overlay.scrollTop = ry * cssHeight - overlay.clientHeight / 2;
      hint.textContent = 'Drag to explore · click or press Esc to go back';
    };
    loader.onerror = () => {
      if (this.#detail !== overlay) return;
      this.#closeDetail();
    };
    loader.src = fullUrl;
  }

  #closeDetail() {
    const overlay = this.#detail;
    if (!overlay) return;
    this.#detail = null;
    overlay.remove();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    this.#bindDetailZoom();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    this.#closeDetail();

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#closeDetail();
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key. While the detail view is open, Esc only closes the
   * detail view and returns to the fitted view.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();

    if (this.#detail) {
      this.#closeDetail();
      return;
    }

    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Adds the detail-view styles once (kept in this file so the customisation is a single-file change).
 */
function ensureDetailStyles() {
  if (document.getElementById('detail-zoom-styles')) return;

  const style = document.createElement('style');
  style.id = 'detail-zoom-styles';
  style.textContent = `
    .detail-zoom {
      position: fixed;
      inset: 0;
      z-index: 1000;
      overflow: auto;
      background: var(--color-background, #fff);
      cursor: grab;
      overscroll-behavior: contain;
      scrollbar-width: none;
    }
    .detail-zoom::-webkit-scrollbar { display: none; }
    .detail-zoom--dragging { cursor: grabbing; user-select: none; }
    .detail-zoom__image {
      display: block;
      max-width: none;
      user-select: none;
      -webkit-user-drag: none;
    }
    .detail-zoom__close {
      position: fixed;
      top: 16px;
      inset-inline-end: 16px;
      width: 44px;
      height: 44px;
      border: 0;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.65);
      color: #fff;
      font-size: 28px;
      line-height: 1;
      cursor: pointer;
    }
    .detail-zoom__hint {
      position: fixed;
      inset-inline: 0;
      bottom: 16px;
      margin: 0 auto;
      width: max-content;
      max-width: calc(100% - 32px);
      padding: 8px 16px;
      border-radius: 999px;
      background: rgba(0, 0, 0, 0.65);
      color: #fff;
      font-size: 14px;
      pointer-events: none;
    }
  `;
  document.head.append(style);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
